import { BaseBuilder, BaseBuilderOptions, IBuilder } from './BaseBuilder.ts';
import { ProductCriteriaOptions } from '@services/MarppleShop/features/ProductList/type/product-list.request.ts';
import { CommonPageType, CommonRankType, CommonFilters } from './shared/common-types';
import { PageManager, RankManager, FilterManager, SortManager } from './managers';

export type BrandDealPage = CommonPageType;
export type BrandDealRankType = CommonRankType;
export type BrandDealBuilderFilters = CommonFilters;

interface BrandDealBuilderOptions extends BaseBuilderOptions {
  page?: BrandDealPage;
  sort?: ProductCriteriaOptions['sort'];
  rankType?: BrandDealRankType;
  filters?: BrandDealBuilderFilters;
}

/**
 * 브랜드딜 전용 URL 빌더
 */
export class BrandDealBuilder implements IBuilder {
  private baseBuilder: BaseBuilder;

  private pageManager: PageManager<BrandDealPage>;
  private rankManager: RankManager<BrandDealRankType>;
  private filterManager: FilterManager<BrandDealBuilderFilters>;
  private sortManager: SortManager;

  // string 타입 기반 URL 매핑
  private static readonly PAGE_URL_MAP: Record<BrandDealPage, string> = {
    RANKING: '/:lang/@/brand-deals/ranking',
    PRODUCTS: '/:lang/@/brand-deals/products',
  };

  constructor(initialOptions: BrandDealBuilderOptions) {
    this.baseBuilder = new BaseBuilder(initialOptions);

    this.pageManager = new PageManager(initialOptions.page, 'RANKING', BrandDealBuilder.PAGE_URL_MAP);

    this.rankManager = new RankManager(initialOptions.rankType, 'REALTIME');

    this.filterManager = new FilterManager(initialOptions.filters, {});

    this.sortManager = new SortManager(initialOptions.sort, 'popular');
  }

  private getBaseUrl(): string {
    const lang = this.baseBuilder.getLangOrThrow();
    return this.pageManager.getPageUrl(lang);
  }

  setPage(page: BrandDealPage): this {
    this.pageManager.setPage(page);
    return this;
  }

  setRankType(rankType: BrandDealRankType): this {
    this.rankManager.setRankType(rankType);
    return this;
  }

  setFilters(filters: Partial<BrandDealBuilderFilters>): this {
    this.filterManager.setFilters(filters);
    return this;
  }

  setSort(sort: ProductCriteriaOptions['sort']): this {
    this.sortManager.setSort(sort);
    return this;
  }

  build(): string {
    const baseUrl = this.getBaseUrl();

    if (this.pageManager.isRanking()) {
      return this.rankManager.buildRankingUrl(baseUrl);
    } else if (this.pageManager.isProducts()) {
      return this.filterManager.buildProductsUrl(baseUrl, this.sortManager.getSort());
    } else {
      return baseUrl;
    }
  }

  clone(): BrandDealBuilder {
    return new BrandDealBuilder({
      lang: this.baseBuilder.getLangOrThrow(),
      cate_root_id: this.baseBuilder.getCateRoot().id,
      page: this.pageManager.getPage(),
      rankType: this.rankManager.getRankType(),
      filters: this.filterManager.getFilters(),
      sort: this.sortManager.getSort(),
    });
  }
}
